import React, { useEffect } from 'react';
import { setPageMeta } from '../utils/seo';
import { Link } from 'react-router-dom';
import {
  Award,
  Users,
  ShieldCheck,
  TrendingUp,
  Heart,
  Target,
  MessageCircle
} from 'lucide-react';

const About = () => {
  useEffect(() => {
    setPageMeta({
      title: 'Sobre Nós',
      description: 'Conheça a Diamond Cars em Ovar. Uma equipa dedicada à venda de viaturas usadas com transparência, garantia e os melhores preços do mercado.',
      path: '/sobre'
    });
  }, []);

  const values = [
    {
      icon: ShieldCheck,
      title: 'Confiança',
      description:
        'transparência total. Cada carro é verificado antes de chegar a si.'
    },
    {
      icon: Award,
      title: 'Qualidade',
      description:
        'veículos em excelente estado, com histórico confirmado e garantia.'
    },
    {
      icon: Heart,
      title: 'Atendimento',
      description:
        'uma equipa dedicada a encontrar o carro ideal para si.'
    },
    {
      icon: TrendingUp,
      title: 'Melhores Preços',
      description:
        'preços justos e competitivos, com opções de financiamento flexíveis.'
    }
  ];

  const stats = [
    { number: '50+', label: 'Carros Vendidos' },
    { number: '95%', label: 'Clientes Satisfeitos' },
    { number: '24/7', label: 'Suporte Disponível' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">Sobre Nós</h1>
          <p className="text-xl text-primary-100 max-w-2xl mx-auto">
            Onde a paixão pelos carros se transforma em confiança para si
          </p>
        </div>
      </section>

      {/* Estatísticas */}
      <section className="container mx-auto px-4 -mt-10">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nossa História */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <Target className="w-12 h-12 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-6">
            A Nossa História
          </h2>
          <div className="space-y-4 text-gray-600 text-lg">
            <p>
              Na <strong className="text-primary-600">Diamond Cars</strong>, cada carro conta uma história e cada cliente merece fazer parte dela.
            </p>
            <p>
              Desde jovem, cresci rodeado pelo mundo automóvel, aprendendo com o meu pai a paixão pelos carros e a importância da confiança e da dedicação.
            </p>
            <p>
              Este projeto é a concretização de um sonho antigo, resultado de uma vida de experiência e dedicação ao setor. Cada venda, cada atendimento, é fruto de anos de conhecimento e amor pelos automóveis.
            </p>
            <p>
              Mais do que vender viaturas, queremos partilhar a nossa paixão. Na Diamond Cars, cada cliente encontra confiança, experiência e o carro certo para a sua história.
            </p>
            <p>
              Não vendemos apenas carros, ajudamos a encontrar histórias sobre rodas.
            </p>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            Os Nossos Valores
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="text-center p-6 rounded-lg hover:shadow-lg transition"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Equipa (opcional - pode ser expandido) */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl p-12 text-center">
          <Users className="w-16 h-16 text-primary-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Uma equipa dedicada
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Na Diamond Cars, contamos com profissionais apaixonados por carros e focados em tornar a sua experiência única. Visite-nos e sinta a diferença de ser atendido por quem realmente ama o que faz.
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center space-x-2 bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition font-semibold"
          >
            <MessageCircle className="w-5 h-5" />
            <span>Fale Connosco</span>
          </Link>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gray-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Pronto para Encontrar o Seu Carro?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Explore o nosso catálogo e descubra as melhores ofertas.
          </p>
          <Link
            to="/"
            className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition font-semibold text-lg"
          >
            Ver Carros Disponíveis
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
